import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Select from "react-select";
import api from "../services/api";
import { login, SUCURSAL, USER_KEY } from "../services/auth";
import { setAuthority } from "../services/authority";
import { useTranslation } from "../i18n/I18nContext";
import useOnlineStatus from "../hook/useOnlineStatus";
import versionInfo from "../version.json";

const sucursalSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 56,
    height: 56,
    paddingLeft: 32,
    background: "var(--neutral-50)",
    borderRadius: 12,
    borderColor: state.isFocused ? "var(--primary-600)" : "var(--neutral-300)",
    boxShadow: state.isFocused ? "0 0 0 1px var(--primary-600)" : "none",
    "&:hover": { borderColor: "var(--primary-600)" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 8px" }),
  placeholder: (base) => ({ ...base, color: "var(--text-secondary-light)" }),
  singleValue: (base) => ({ ...base, color: "var(--neutral-900)" }),
  input: (base) => ({ ...base, color: "var(--neutral-900)" }),
  menu: (base) => ({
    ...base,
    background: "var(--neutral-50)",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
    zIndex: 20,
  }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected
      ? "var(--primary-600)"
      : state.isFocused
      ? "rgba(72,127,255,0.1)"
      : "transparent",
    color: state.isSelected ? "#fff" : "var(--neutral-900)",
    cursor: "pointer",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "var(--text-secondary-light)" }),
};

const SignInLayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // step: 'credentials' | 'branch' | 'password'
  const [step, setStep] = useState("credentials");
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [autoLogin, setAutoLogin] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const isOnline = useOnlineStatus();

  const [sucursals, setSucursals] = useState([]);
  const [sucursal, setSucursal] = useState(null);

  const [newPass, setNewPass] = useState("");
  const [newPassVisible, setNewPassVisible] = useState(false);
  const [repeatPass, setRepeatPass] = useState("");
  const [repeatPassVisible, setRepeatPassVisible] = useState(false);

  // Mostra o aviso apenas quando chegamos aqui via useIdleLogout
  // (MasterLayout), não em todos os acessos normais à página de login.
  useEffect(() => {
    if (location.state?.reason === "inactivity") {
      toast.info(t("auth.session.expired.inactivity"));
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redirectAfterLogin = (userDetails) => {
    localStorage.setItem(SUCURSAL, JSON.stringify(userDetails.sucursals[0]));
    navigate("/dashboard");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userName || !password) {
      toast.error("Preencha o nome de utilizador e a password.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post("/api/auth", { userName, password });

      login(response.data);
      const userDetails = await api.get(`/api/user/unique/${userName}`);
      localStorage.setItem(USER_KEY, JSON.stringify(userDetails.data));
      setAuthority(userDetails.data.authority);

      if (!userDetails.data.passwordUpdated) {
        setStep("password");
      } else if (userDetails.data.sucursals.length > 1) {
        setSucursals(userDetails.data.sucursals);
        setStep("branch");
      } else {
        redirectAfterLogin(userDetails.data);
      }
    } catch (err) {
      if (err?.message?.includes("ENOTFOUND") || err?.message?.includes("Network Error")) {
        toast.error("Servidor indisponível. Verifique a sua ligação à internet.");
      } else if (err?.response?.data?.includes?.("Invalid username")) {
        toast.error("Utilizador ou password inválidos.");
      } else {
        toast.error(err?.message || "Ocorreu um erro ao processar o pedido.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const sucursalOptions = sucursals.map((s) => ({
    value: s.id,
    label: s.name || s.description,
    raw: s,
  }));

  const handleSelectSucursal = (option) => {
    setSucursal(option);
  };

  const handleGoToHomePage = () => {
    if (!sucursal) {
      toast.error("Seleccione a SUCURSAL.");
      return;
    }
    localStorage.setItem(SUCURSAL, JSON.stringify(sucursal.raw));
    navigate("/dashboard");
  };

  const handleChangePassword = async () => {
    if (!newPass || !repeatPass || newPass !== repeatPass) {
      toast.error("As passwords introduzidas não coincidem.");
      return;
    }
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return;
    const user = JSON.parse(userStr);
    try {
      await api.put(`/api/user/password/${user.id}`, { password: newPass, updatedBy: 1 });
      toast.success("Password alterada com sucesso.");

      const refreshed = JSON.parse(localStorage.getItem(USER_KEY));
      if (refreshed.sucursals.length > 1) {
        setSucursals(refreshed.sucursals);
        setStep("branch");
      } else {
        redirectAfterLogin(refreshed);
      }
    } catch (err) {
      toast.error(err?.message || "Não foi possível alterar a password.");
    }
  };

  return (
    <section className='auth bg-base d-flex flex-wrap'>
      <ToastContainer />
      <div className='auth-left d-lg-block d-none position-relative'>
        <img src='assets/images/sge-pro-banner-gestao-estudantil.png' alt='SGE-PRO - Sistema de Gestão Estudantil' />
        <p className='position-absolute start-0 bottom-0 ms-32 mb-4 text-secondary-light text-sm'>
          Versão {versionInfo.version} {versionInfo.date}
        </p>
      </div>
      <div className='auth-right py-32 px-24 d-flex flex-column justify-content-center'>
        <div className='max-w-464-px mx-auto w-100'>
          <div>
            <Link to='/' className='mb-40 max-w-290-px d-block'>
              <img src='assets/images/logo.png' alt='SGE-PRO' style={{ maxHeight: 72, width: 'auto' }} />
            </Link>
            <h4 className='mb-12'>Entrar na sua Conta</h4>
            <p className='mb-32 text-secondary-light text-lg'>
              {step === "credentials" && "Bem-vindo de volta! Introduza os seus dados"}
              {step === "branch" && "Seleccione a SUCURSAL com que pretende entrar"}
              {step === "password" && "É o seu primeiro acesso. Defina uma nova password"}
            </p>
          </div>

          {step === "credentials" && (
            <form onSubmit={handleSubmit}>
              {!isOnline && (
                <div className='alert alert-warning py-12 px-16 radius-8 mb-16'>
                  Sem ligação à internet. Verifique a sua rede para poder entrar.
                </div>
              )}
              <div className='icon-field mb-16'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='mage:email' />
                </span>
                <input
                  type='text'
                  className='form-control h-56-px bg-neutral-50 radius-12'
                  placeholder='Utilizador'
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  required
                />
              </div>
              <div className='position-relative mb-20'>
                <div className='icon-field'>
                  <span className='icon top-50 translate-middle-y'>
                    <Icon icon='solar:lock-password-outline' />
                  </span>
                  <input
                    type={passwordVisible ? "text" : "password"}
                    className='form-control h-56-px bg-neutral-50 radius-12'
                    id='your-password'
                    placeholder='Password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <span
                  className={`toggle-password ${passwordVisible ? "ri-eye-off-line" : "ri-eye-line"} cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                  onClick={() => setPasswordVisible(!passwordVisible)}
                />
              </div>
              <div className=''>
                <div className='d-flex justify-content-between gap-2'>
                  <div className='form-check style-check d-flex align-items-center'>
                    <input
                      className='form-check-input border border-neutral-300'
                      type='checkbox'
                      checked={autoLogin}
                      onChange={(e) => setAutoLogin(e.target.checked)}
                      id='remeber'
                    />
                    <label className='form-check-label' htmlFor='remeber'>
                      Lembrar-me{" "}
                    </label>
                  </div>
                  <Link to='/marcar-presenca' className='text-primary-600 fw-medium'>
                    Marcar Presença
                  </Link>
                </div>
              </div>
              <button
                type='submit'
                className='btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32'
                disabled={submitting || !isOnline}
              >
                {submitting ? "A entrar..." : "Entrar"}
              </button>
            </form>
          )}

          {step === "branch" && (
            <div>
              <div className='mb-20'>
                <label className='form-label fw-semibold text-secondary-light'>
                  SUCURSAL
                </label>
                <div className='position-relative'>
                  <span
                    className='icon position-absolute top-50 translate-middle-y z-1 text-secondary-light'
                    style={{ left: 12 }}
                  >
                    <Icon icon='solar:buildings-2-outline' />
                  </span>
                  <Select
                    classNamePrefix='sucursal-select'
                    styles={sucursalSelectStyles}
                    options={sucursalOptions}
                    value={sucursal}
                    onChange={handleSelectSucursal}
                    placeholder='Seleccione a SUCURSAL...'
                    noOptionsMessage={() => "Sem SUCURSAIS disponíveis"}
                    isSearchable
                  />
                </div>
              </div>
              <button
                type='button'
                className='btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12'
                onClick={handleGoToHomePage}
              >
                Entrar
              </button>
            </div>
          )}

          {step === "password" && (
            <div>
              <div className='mb-20'>
                <label className='form-label fw-semibold text-secondary-light'>
                  Nova password
                </label>
                <div className='position-relative'>
                  <div className='icon-field'>
                    <span className='icon top-50 translate-middle-y'>
                      <Icon icon='solar:lock-password-outline' />
                    </span>
                    <input
                      type={newPassVisible ? "text" : "password"}
                      className='form-control h-56-px bg-neutral-50 radius-12'
                      placeholder='Nova password'
                      value={newPass}
                      onChange={(e) => setNewPass(e.target.value)}
                    />
                  </div>
                  <span
                    className={`toggle-password ${newPassVisible ? "ri-eye-off-line" : "ri-eye-line"} cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                    onClick={() => setNewPassVisible(!newPassVisible)}
                  />
                </div>
              </div>
              <div className='mb-20'>
                <label className='form-label fw-semibold text-secondary-light'>
                  Repetir password
                </label>
                <div className='position-relative'>
                  <div className='icon-field'>
                    <span className='icon top-50 translate-middle-y'>
                      <Icon icon='solar:lock-password-outline' />
                    </span>
                    <input
                      type={repeatPassVisible ? "text" : "password"}
                      className='form-control h-56-px bg-neutral-50 radius-12'
                      placeholder='Repita a nova password'
                      value={repeatPass}
                      onChange={(e) => setRepeatPass(e.target.value)}
                    />
                  </div>
                  <span
                    className={`toggle-password ${repeatPassVisible ? "ri-eye-off-line" : "ri-eye-line"} cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                    onClick={() => setRepeatPassVisible(!repeatPassVisible)}
                  />
                </div>
              </div>
              <button
                type='button'
                className='btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12'
                onClick={handleChangePassword}
              >
                Alterar Password
              </button>
            </div>
          )}
        </div>

        <p className='d-lg-none text-center text-secondary-light text-sm mt-auto mb-0'>
          Versão {versionInfo.version} {versionInfo.date}
        </p>
      </div>
    </section>
  );
};

export default SignInLayer;
